import React, { useRef, useState, useEffect, useId } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";

/**
 * BaseBadge - The Single Source of Truth for all new Carousel UI.
 * Enforces rigid aspect ratios, premium glassmorphism, and handles overflow internal scrolling.
 */
const BaseBadge = ({
  children,
  title, // New prop for centralized header
  className = "",
  duration = 6,
  borderWidth = 1.5,
  colorFrom = "#22d3ee", // Cyan default
  colorTo = "#3b82f6",   // Blue default
  glowColor = "rgba(34, 211, 238, 0.3)",
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const instanceId = useId();
  const gradientId = `beam-grad-${instanceId}`;
  const filterId = `beam-glow-${instanceId}`;

  useEffect(() => {
    if (containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current;
      setDimensions({ width: offsetWidth, height: offsetHeight });
    }

    const handleResize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setDimensions({ width: offsetWidth, height: offsetHeight });
      }
    };

    window.addEventListener("resize", handleResize);
    // Add a small delay to ensure rendering is complete before measuring
    const timeoutId = setTimeout(handleResize, 100);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const perimeter = 2 * (dimensions.width + dimensions.height);

  return (
    <motion.div
      ref={containerRef}
      // CRITICAL ARCHITECTURE: Enforce strict dimensions so carousel never breaks.
      className={cn(
        "relative overflow-visible rounded-3xl w-full max-w-[420px] h-[550px] mx-auto",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Outer Glow Effect on Hover */}
      <motion.div
        className="absolute -inset-4 rounded-[2rem] pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${colorFrom}50, ${colorTo}40)`,
        }}
        initial={{ filter: "blur(24px)", opacity: 0.2 }}
        animate={{ 
          opacity: isHovered ? 0.35 : 0.2, 
          filter: isHovered ? "blur(12px)" : "blur(24px)",
          scale: isHovered ? 0.98 : 1
        }}
        transition={{ duration: 0.4 }}
      />

      {/* Main Glass Container */}
      <motion.div 
        className="relative h-full w-full rounded-3xl overflow-hidden backdrop-blur-2xl shadow-2xl flex flex-col"
        animate={{
          backgroundColor: isHovered ? "rgba(11, 17, 33, 0.92)" : "rgba(11, 17, 33, 0.8)",
          borderColor: isHovered ? "rgba(255, 255, 255, 0.35)" : "rgba(255, 255, 255, 0.1)",
          borderWidth: "1px",
          borderStyle: "solid"
        }}
        transition={{ duration: 0.3 }}
      >
        {/* Base Gradient Layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-white/[0.02] to-transparent pointer-events-none" />

        {/* Inner Top Highlight */}
        <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] pointer-events-none" />

        {/* Noise Texture */}
        <div
          className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Animated Border Beam (Only draws if dimensions exist) */}
        {dimensions.width > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            <svg
              className="absolute inset-0 h-full w-full overflow-visible"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor={colorFrom} stopOpacity="0" />
                  <stop offset="30%" stopColor={colorFrom} stopOpacity="1" />
                  <stop offset="70%" stopColor={colorTo} stopOpacity="1" />
                  <stop offset="100%" stopColor={colorTo} stopOpacity="0" />
                </linearGradient>
                <filter id={filterId}>
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="24"
                ry="24"
                fill="none"
                stroke={`url(#${gradientId})`}
                strokeWidth={borderWidth + 1}
                strokeDasharray="0.15 0.85"
                pathLength="1"
                strokeLinecap="round"
                filter={`url(#${filterId})`}
                style={{
                  animation: `badge-beam-travel ${duration}s linear infinite`,
                }}
              />
            </svg>
          </div>
        )}

        {/* Corner Accent Glows (Diminished on hover for clarity) */}
        <motion.div
          className="absolute -top-20 -left-20 w-40 h-40 rounded-full blur-3xl pointer-events-none"
          style={{ background: glowColor }}
          animate={{ opacity: isHovered ? 0.15 : 0.4 }}
        />
        <motion.div
          className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl pointer-events-none"
          style={{ background: glowColor }}
          animate={{ opacity: isHovered ? 0.05 : 0.2 }}
        />

        {/* Content Area - Scrollable internally if necessary to preserve badge height */}
        <div className="relative z-10 p-8 flex-grow overflow-y-auto no-scrollbar flex flex-col">
          {title && (
            <>
              <h2 className="text-sm font-medium tracking-[0.2em] font-header text-white/50 mb-4 uppercase">
                {title}
              </h2>
              <div 
                className="w-12 h-0.5 mb-6 shrink-0" 
                style={{ 
                  background: `linear-gradient(to right, ${colorFrom}80, transparent)` 
                }} 
              />
            </>
          )}
          {children}
        </div>

        {/* CSS Animation Keyframes & Scrollbar hiding */}
        <style>{`
          @keyframes badge-beam-travel {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -1; }
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </motion.div>
    </motion.div>
  );
};

export default BaseBadge;
